import React, { useState, useMemo } from "react";
import { useChartTextColor } from "../hooks/useChartTextColor";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import type { TrustCallAdvancedInsight } from "../types";
import { field } from "../utils/insightField";
import { parseTsToDate, safeParse, toIsoDate, formatLocal } from "../utils/dateHelpers";

interface VerizonAnalyticsTabProps {
  data: TrustCallAdvancedInsight;
}

// === Reputation category lookup — matches MVC reputationCategories ===
const REPUTATION_CATEGORIES: Record<number, string> = {
  0: "Not Yet Scored",
  1: "Spam",
  2: "Fraud/Scam",
  3: "Unwanted",
  4: "Nuisance",
  5: "Unknown Caller",
  6: "Legitimate",
};

// === Reason lookup — matches MVC reasonLookup ===
const REASON_LOOKUP: Record<string, string> = {
  "1": "Crowdsourced Reports",
  "2": "Call Pattern Analysis",
  "3": "Known Bad Actor",
  "4": "Override Applied",
  "5": "Carrier Data",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// === Data parsing (matches MVC parseDailyTimeline, parseReputationHistory, parseOverrideEvents) ===

interface TimelineDay {
  date: Date;
  vol: number;
  spam: number;
  label: string; // "May 18"
}

interface RepEvent {
  date: Date;
  category: string | null;
  score: number | null;
  reasonId: string | null;
}

interface OverrideEvent {
  date: Date;
  action: string;
  category: string;
  by: string;
  note: string;
}

interface RepChartPoint {
  date: Date;
  label: string;
  score: number;
  color: string;
  source: string;
  tooltipText: string;
}

function parseDailyTimeline(
  row: TrustCallAdvancedInsight,
  startDate: Date,
  endDate: Date
): TimelineDay[] {
  const f = (name: string) => field(row as Record<string, unknown>, name);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callVolRaw: any[] = (safeParse(f("CallVolJson")) as any[]) || [];
  const callVolData = callVolRaw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((p: any) => {
      const dt = parseTsToDate(p.date || p.ts || p.timestamp);
      return { date: dt, vol: (p.callVol || p.count || p.volume || 0) as number };
    })
    .filter((p): p is { date: Date; vol: number } => p.date != null && !isNaN(p.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  // Use crowdSourceStatJSON (aggregated daily stats) for charts; fallback to crowdSourceJSON
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const crowdStats = (safeParse(f("CrowdSourceStatJSON")) as any[]) || [];
  let spamComplaints: { date: Date; count: number }[];

  if (Array.isArray(crowdStats) && crowdStats.length > 0) {
    spamComplaints = crowdStats
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((s: any) => { const t = String(s.tag || "").toLowerCase(); return t && t !== "not-spam"; })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((s: any) => ({ date: parseTsToDate(s.statsTs)!, count: (s.count || 1) as number }))
      .filter((r) => r.date != null);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let crowdRaw: any[] = [];
    try {
      const parsed = safeParse(f("CrowdSourceJSON"));
      if (Array.isArray(parsed)) crowdRaw = parsed;
    } catch { /* ignore */ }
    spamComplaints = crowdRaw
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((e: any) => { const cat = String(e.csucat || "").toLowerCase(); return cat && cat !== "not-spam"; })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((e: any) => {
        const ms = e.ts != null ? e.ts : e.eventTs;
        return { date: parseTsToDate(ms != null && !isNaN(Number(ms)) ? Number(ms) : null)!, count: 1 };
      })
      .filter((r) => r.date != null);
  }

  const dayMap: Record<string, TimelineDay> = {};
  callVolData.forEach((d) => {
    const key = toIsoDate(d.date);
    if (!dayMap[key]) dayMap[key] = { date: new Date(key), vol: 0, spam: 0, label: "" };
    dayMap[key].vol = d.vol;
  });
  spamComplaints.forEach((d) => {
    const key = toIsoDate(d.date);
    if (!dayMap[key]) dayMap[key] = { date: new Date(key), vol: 0, spam: 0, label: "" };
    dayMap[key].spam += d.count;
  });

  let timeline = Object.keys(dayMap)
    .sort()
    .map((k) => dayMap[k]);

  // Filter by date range
  timeline = timeline.filter((d) => d.date >= startDate && d.date <= endDate);

  // Add labels
  timeline.forEach((d) => {
    d.label = MONTHS[d.date.getUTCMonth()] + " " + d.date.getUTCDate();
  });

  return timeline;
}

function parseReputationHistory(row: TrustCallAdvancedInsight): RepEvent[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f2 = (name: string) => field(row as Record<string, unknown>, name);
  const repRaw = (safeParse(f2("ReputationHistoryJson")) as any[]) || [];
  return repRaw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((item: any) => {
      const tsRaw = item.eventTS || item.eventTs || item.ts || item.timestamp || null;
      const dt = parseTsToDate(tsRaw);
      const cat = item.tagHistory ? item.tagHistory.category : null;
      const score = item.tagHistory ? item.tagHistory.score : null;
      const reasonId = item.reasonId != null ? String(item.reasonId) : null;
      return { date: dt!, category: cat, score: score as number | null, reasonId };
    })
    .filter((r) => r.date != null && !isNaN(r.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

function parseOverrideEvents(row: TrustCallAdvancedInsight): OverrideEvent[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f3 = (name: string) => field(row as Record<string, unknown>, name);
  const overridesRaw = (safeParse(f3("OverrideAuditJSON")) as any[]) || [];
  return overridesRaw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((e: any) => {
      const dt = parseTsToDate(e.eventTS || e.eventTime || e.updatedDate || e.createdDate);
      const action = e.action === "I" ? "Added" : e.action === "D" ? "Deleted" : (e.action || "\u2014");
      const cat = REPUTATION_CATEGORIES[Number(e.categoryId)] || (e.categoryId != null ? "Cat " + e.categoryId : "\u2014");
      const by = (e.updatedBy || e.createdBy || "\u2014") as string;
      return { date: dt!, action, category: cat, by, note: (e.note || "") as string };
    })
    .filter((r) => r.date != null && !isNaN(r.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

function buildRepChartData(
  timeline: TimelineDay[],
  repHistory: RepEvent[],
  overrideEvents: OverrideEvent[]
): RepChartPoint[] {
  const timelineDateKeys = new Set(timeline.map((d) => toIsoDate(d.date)));

  // Override active checker
  const overrideSorted = [...overrideEvents].sort((a, b) => a.date.getTime() - b.date.getTime());
  function isOverrideActiveAt(dt: Date): boolean {
    let active = false;
    for (const o of overrideSorted) {
      if (o.date > dt) break;
      active = o.action === "Added";
    }
    return active;
  }

  const data: RepChartPoint[] = [];

  // Algorithm-driven reputation changes
  repHistory.forEach((r) => {
    const dateKey = toIsoDate(r.date);
    if (!timelineDateKeys.has(dateKey)) return;
    const catName = REPUTATION_CATEGORIES[Number(r.category)] || ("Cat " + (r.category || "?"));
    const reasonText = r.reasonId && REASON_LOOKUP[r.reasonId] ? REASON_LOOKUP[r.reasonId] : (r.reasonId ? "Reason " + r.reasonId : "\u2014");
    const isNegative = r.score != null && r.score < 0;
    const overridden = isNegative && isOverrideActiveAt(r.date);
    const scoreLabel = isNegative ? "Spam" : "Clean";
    let color = isNegative ? "#DC3545" : "#5B9BD5";
    if (overridden) color = "#6F42C1";
    const tooltip = `${catName} / ${scoreLabel} (${r.score ?? "?"})\nReason: ${reasonText}`;

    data.push({
      date: new Date(dateKey),
      label: MONTHS[new Date(dateKey).getUTCMonth()] + " " + new Date(dateKey).getUTCDate(),
      score: r.score || 0,
      color,
      source: overridden ? "Overridden" : "Algorithm",
      tooltipText: tooltip,
    });
  });

  // Override events
  overrideEvents.forEach((o) => {
    const dateKey = toIsoDate(o.date);
    if (!timelineDateKeys.has(dateKey)) return;
    const isAdded = o.action === "Added";
    data.push({
      date: new Date(dateKey),
      label: MONTHS[new Date(dateKey).getUTCMonth()] + " " + new Date(dateKey).getUTCDate(),
      score: isAdded ? 2 : -2,
      color: isAdded ? "#0d6efd" : "#DC3545",
      source: isAdded ? "OverrideAdded" : "OverrideDeleted",
      tooltipText: `Override ${o.action}\n${o.category}\nBy: ${o.by}${o.note ? "\nNote: " + o.note : ""}`,
    });
  });

  data.sort((a, b) => a.date.getTime() - b.date.getTime());
  return data;
}

// Expand timeline to include dates from rep history + overrides so dots appear on chart axis
function expandTimelineWithEvents(
  timeline: TimelineDay[],
  repHistory: RepEvent[],
  overrideEvents: OverrideEvent[]
): void {
  if (timeline.length === 0) return;
  const minDate = timeline[0].date;
  const maxDate = timeline[timeline.length - 1].date;
  const existingKeys = new Set(timeline.map((d) => toIsoDate(d.date)));

  function addIfInRange(dt: Date | null) {
    if (!dt || isNaN(dt.getTime())) return;
    const key = toIsoDate(dt);
    if (existingKeys.has(key)) return;
    const dayDate = new Date(key);
    if (dayDate >= minDate && dayDate <= maxDate) {
      timeline.push({ date: dayDate, vol: 0, spam: 0, label: MONTHS[dayDate.getUTCMonth()] + " " + dayDate.getUTCDate() });
      existingKeys.add(key);
    }
  }

  repHistory.forEach((r) => addIfInRange(r.date));
  overrideEvents.forEach((o) => addIfInRange(o.date));
  timeline.sort((a, b) => a.date.getTime() - b.date.getTime());
}

// Build spam status checker from reputation + override events
function buildSpamStatusMap(repHistory: RepEvent[], overrideEvents: OverrideEvent[]) {
  const stateChanges: { date: Date; type: string; isSpam?: boolean }[] = [];
  repHistory.forEach((e) => {
    stateChanges.push({ date: e.date, type: "algorithm", isSpam: e.score != null && e.score < 0 });
  });
  overrideEvents.forEach((o) => {
    if (o.action === "Added") stateChanges.push({ date: o.date, type: "overrideOn", isSpam: false });
    else if (o.action === "Deleted") stateChanges.push({ date: o.date, type: "overrideOff" });
  });
  stateChanges.sort((a, b) => a.date.getTime() - b.date.getTime());

  return function isSpamOnDate(dateKey: string): boolean {
    const targetDate = new Date(dateKey);
    let lastAlgoState = false;
    let overrideActive = false;
    for (const sc of stateChanges) {
      if (sc.date > targetDate) break;
      if (sc.type === "algorithm") lastAlgoState = !!sc.isSpam;
      else if (sc.type === "overrideOn") overrideActive = true;
      else if (sc.type === "overrideOff") overrideActive = false;
    }
    return overrideActive ? false : lastAlgoState;
  };
}

// === Custom tooltip for bar charts ===
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function BarTooltip({ active, payload, label, unit }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", color: "#333", border: "1px solid #ccc", padding: "4px 8px", fontSize: 12 }}>
      <strong>{label}</strong>: {payload[0].value} {unit}
    </div>
  );
}

// === Legend ===
const LEGEND_ITEMS = [
  { color: "#5B9BD5", label: "Clean" },
  { color: "#DC3545", label: "Spam" },
  { color: "#6F42C1", label: "Spam (Overridden to Clean)" },
  { color: "#0d6efd", label: "Override Event" },
];

function RepLegend() {
  return (
    <div style={{ textAlign: "center", fontSize: 12, marginTop: 4, marginBottom: 8 }}>
      {LEGEND_ITEMS.map((item) => (
        <span key={item.label} style={{ marginLeft: 16 }}>
          <span style={{
            display: "inline-block", width: 12, height: 12,
            background: item.color, borderRadius: "50%", verticalAlign: "middle", marginRight: 4,
          }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

// === Main Component ===
const VerizonAnalyticsTab: React.FC<VerizonAnalyticsTabProps> = ({ data: row }) => {
  const textColor = useChartTextColor();
  // Date range state — default: last 90 days of available data
  const fullTimeline = useMemo(
    () => parseDailyTimeline(row, new Date("2000-01-01"), new Date("2099-12-31")),
    [row]
  );
  const repHistory = useMemo(() => parseReputationHistory(row), [row]);
  const overrideEvents = useMemo(() => parseOverrideEvents(row), [row]);

  const dataMinDate = fullTimeline.length > 0 ? fullTimeline[0].date : new Date();
  const dataMaxDate = fullTimeline.length > 0 ? fullTimeline[fullTimeline.length - 1].date : new Date();

  const defaultEnd = dataMaxDate;
  const defaultStartCalc = new Date(defaultEnd);
  defaultStartCalc.setDate(defaultStartCalc.getDate() - 89);
  const defaultStart = defaultStartCalc < dataMinDate ? dataMinDate : defaultStartCalc;

  const [startDate, setStartDate] = useState(toIsoDate(defaultStart));
  const [endDate, setEndDate] = useState(toIsoDate(defaultEnd));
  const [highlightSpam, setHighlightSpam] = useState(false);

  // TNS Score cards
  const fc = (name: string) => field(row as Record<string, unknown>, name);
  const tnsScore = fc("TNRMScore") as number | null | undefined;
  const tnsLastRetrieved = fc("TNRMScoreRetrievedOn") ? parseTsToDate(fc("TNRMScoreRetrievedOn")) : null;
  const scoreVal = tnsScore != null ? (tnsScore > 0 ? "+" : "") + tnsScore : "\u2014";
  const scoreClass = tnsScore == null ? "" : tnsScore >= 0 ? "text-success" : "text-danger";

  // Filtered timeline + chart data
  const { timeline, volData, spamData, repData, dateRangeNotice } = useMemo(() => {
    const sd = new Date(startDate);
    const ed = new Date(endDate);
    const tl = parseDailyTimeline(row, sd, ed);

    expandTimelineWithEvents(tl, repHistory, overrideEvents);

    const isSpamOnDate = buildSpamStatusMap(repHistory, overrideEvents);
    const vol = tl.map((d) => {
      const key = toIsoDate(d.date);
      return { label: d.label, value: d.vol, color: highlightSpam && isSpamOnDate(key) ? "#DC3545" : "#5B9BD5" };
    });
    const spam = tl.map((d) => ({ label: d.label, value: d.spam, color: "#DC3545" }));
    const rep = buildRepChartData(tl, repHistory, overrideEvents);

    // Date range notice
    let notice = "";
    const startClamped = sd < dataMinDate;
    const endClamped = ed > dataMaxDate;
    if (startClamped || endClamped) {
      const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      notice = `Data is only available from ${fmt(dataMinDate)} to ${fmt(dataMaxDate)}`;
    }

    return { timeline: tl, volData: vol, spamData: spam, repData: rep, dateRangeNotice: notice };
  }, [row, startDate, endDate, highlightSpam, repHistory, overrideEvents, dataMinDate, dataMaxDate]);

  const handleReset = () => {
    setStartDate(toIsoDate(defaultStart));
    setEndDate(toIsoDate(defaultEnd));
  };

  if (fullTimeline.length === 0) {
    return <div className="alert alert-info mb-0">No Verizon analytics data available for this number.</div>;
  }

  const labelStep = Math.max(1, Math.floor(timeline.length / 15));

  // Nice-number algorithm for Y-axis ticks matching Kendo auto-scaling
  const getNiceTicks = (values: number[]): { ticks: number[]; max: number } => {
    const maxVal = Math.max(...values, 0);
    if (maxVal <= 0) return { ticks: [0], max: 0 };
    const roughStep = maxVal / 5;
    const mag = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const res = roughStep / mag;
    let step: number;
    if (res <= 1.5) step = mag;
    else if (res <= 3) step = 2 * mag;
    else if (res <= 7) step = 5 * mag;
    else step = 10 * mag;
    const niceMax = Math.ceil(maxVal / step) * step;
    return { ticks: Array.from({ length: niceMax / step + 1 }, (_, i) => i * step), max: niceMax };
  };
  const volTicks = getNiceTicks(volData.map((d) => d.value));
  const spamTicksCalc = getNiceTicks(spamData.map((d) => d.value));

  return (
    <div style={{ paddingTop: 10 }}>
      {/* TNS Score cards */}
      <div className="row mb-4 mx-1">
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">TNS Score</h5>
              <h2 className={scoreClass}>{scoreVal}</h2>
              <small className="text-muted">Range: -4 to +4</small>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title">TNS Score Last Retrieved</h5>
              <p className="mb-0">{formatLocal(tnsLastRetrieved)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Date range filter */}
      <div className="d-flex align-items-center mb-2 mx-1" style={{ gap: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>Date Range:</label>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 150 }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <span style={{ fontSize: 13 }}>to</span>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 150 }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <button className="btn btn-sm btn-outline-secondary" onClick={handleReset}>Reset</button>
        <div className="form-check form-switch ms-3 d-inline-flex align-items-center" style={{ fontSize: 16 }}>
          <input
            className="form-check-input"
            type="checkbox"
            id="vz2-spam-highlight"
            checked={highlightSpam}
            onChange={(e) => setHighlightSpam(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          <label className="form-check-label ms-1" htmlFor="vz2-spam-highlight" style={{ cursor: "pointer" }}>
            Highlight spam days
          </label>
        </div>
      </div>

      {dateRangeNotice && (
        <div className="alert alert-warning d-inline-block py-1 px-2 mb-2" style={{ fontSize: "0.85rem" }}>
          {dateRangeNotice}
        </div>
      )}

      {timeline.length === 0 ? (
        <div className="alert alert-info mb-0">No data in selected date range.</div>
      ) : (
        <>
          {/* Call Volume Chart */}
          <h6 style={{ fontSize: 14, textAlign: "center" }}>
            Daily Call Volume ({timeline.length} Days)
          </h6>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={volData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: textColor }}
                angle={-45}
                textAnchor="end"
                interval={labelStep - 1}
                height={50}
              />
              <YAxis tick={{ fontSize: 11, fill: textColor }} ticks={volTicks.ticks} domain={[0, volTicks.max]} label={{ value: "Calls", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: textColor } }} />
              <Tooltip content={<BarTooltip unit="calls" />} />
              <Bar dataKey="value" name="Call Volume" barSize={timeline.length > 60 ? 4 : 8}>
                {volData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Spam Reports Chart */}
          <h6 style={{ fontSize: 14, textAlign: "center", marginTop: 5 }}>
            Spam Reports ({timeline.length} Days)
          </h6>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={spamData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: textColor }}
                angle={-45}
                textAnchor="end"
                interval={labelStep - 1}
                height={50}
              />
              <YAxis tick={{ fontSize: 11, fill: textColor }} ticks={spamTicksCalc.ticks} domain={[0, spamTicksCalc.max]} label={{ value: "Reports", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: textColor } }} />
              <Tooltip content={<BarTooltip unit="report(s)" />} />
              <Bar dataKey="value" name="Spam Reports" barSize={timeline.length > 60 ? 4 : 8}>
                {spamData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Reputation Changes Timeline */}
          <h6 style={{ marginTop: 12, fontSize: 14, textAlign: "center" }}>
            Reputation Changes
          </h6>
          {repData.length === 0 ? (
            <div className="alert alert-info mb-0 mt-2">No reputation change history available.</div>
          ) : (
            <div style={{ position: "relative", overflowX: "auto", padding: "8px 0" }}>
              <div style={{ position: "relative", height: 70, margin: "0 30px" }}>
                {/* Horizontal line */}
                <div style={{ position: "absolute", top: 20, left: 0, right: 0, height: 2, background: "#ccc" }} />

                {timeline.map((day, i) => {
                  const count = timeline.length;
                  const pct = count > 1 ? (i / (count - 1)) * 100 : 50;
                  const showLabel = (i % labelStep === 0) || i === count - 1;
                  const dayKey = toIsoDate(day.date);

                  // Find matching rep events for this day
                  const dayEvents = repData.filter((d) => toIsoDate(d.date) === dayKey);
                  const lastEvent = dayEvents.length > 0 ? dayEvents[dayEvents.length - 1] : null;

                  return (
                    <div
                      key={i}
                      style={{ position: "absolute", left: `${pct}%`, transform: "translateX(-50%)", textAlign: "center" }}
                    >
                      {/* Event dot */}
                      {lastEvent && (
                        <div
                          title={lastEvent.tooltipText.replace(/\n/g, " | ")}
                          style={{
                            position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)",
                            width: 12, height: 12, borderRadius: "50%",
                            background: lastEvent.color, cursor: "pointer",
                            border: "2px solid #fff", boxShadow: `0 0 0 1px ${lastEvent.color}`, zIndex: 2,
                          }}
                        />
                      )}
                      {/* Tick */}
                      <div style={{
                        position: "absolute", top: 22, left: "50%", transform: "translateX(-50%)",
                        width: 1, height: lastEvent ? 10 : 6,
                        background: lastEvent ? "#666" : "#ccc",
                      }} />
                      {/* Label */}
                      {showLabel && (
                        <div style={{
                          position: "absolute", top: 36, left: "50%", transform: "translateX(-50%)",
                          fontSize: 9, color: "#666", whiteSpace: "nowrap",
                        }}>
                          {day.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <RepLegend />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VerizonAnalyticsTab;
